import React, { useContext, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import InfoTooltip from "@components/common/InfoTooltip";
import CustomSelect from "@components/common/CustomSelect";
import { LS_PRODUCT_TYPE } from "./common/commonData";
import { toast } from "@components/ui/use-toast";
import PreviewLogo from "../plugin/PreviewLogo";
import PluginLogoCropDialog from "../plugin/PluginLogoCropDialog";
import { Trash2, UploadCloud } from "lucide-react";
import Confirmation from "@components/common/ConfirmDialog";
import { useQuery } from "react-query";
import { loadJsonData } from "@services/api/commonApis";
import { GeneralContext } from "@services/providers/GeneralProvider";

interface GeneralInfoFormProps {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: { minimum: string };
  type: string;
  author: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: { name: string; id: string }[];
  pluginLogo?: string;
  onChange: (fields: Partial<GeneralInfoFormProps>) => void;
  showValidation?: boolean;
}

export const GeneralInfoForm: React.FC<GeneralInfoFormProps> = ({
  pluginName,
  version,
  tags,
  description1,
  description2,
  ls_version,
  type,
  author,
  productType,
  ls_product_type,
  pluginDependency,
  pluginLogo,
  onChange,
  showValidation = false,
}) => {
  const [tagInput, setTagInput] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [previewLogo, setPreviewLogo] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openCropDialog, setOpenCropDialog] = React.useState(false);
  const [cropSourceImage, setCropSourceImage] = React.useState("");
  const { isAdminUser } = useContext(GeneralContext);

  // Set type to "custom" for non-admin users
  useEffect(() => {
    if (!isAdminUser && type !== "custom") {
      onChange({ type: "custom" });
    }
  }, [isAdminUser]);

  // Validation errors
  const errors = React.useMemo(() => {
    if (!showValidation) return {};

    return {
      pluginName: !pluginName?.trim() ? "Plugin name is required" : "",
      version: !version?.trim() ? "Version is required" : "",
      tags: !tags || tags.length === 0 ? "At least one tag is required" : "",
      description1: !description1?.trim()
        ? "Plugin description is required"
        : description1.length < 15
          ? "Description must be at least 15 characters"
          : "",
      description2: !description2?.trim()
        ? "Developer description is required"
        : description2.length < 15
          ? "Description must be at least 15 characters"
          : "",
      ls_version: !ls_version?.minimum ? "LS version is required" : "",
      type: !type?.trim() ? "Type is required" : "",
      author: !author?.trim() ? "Author is required" : "",
      productType: !productType?.trim() ? "Product type is required" : "",
      ls_product_type:
        !ls_product_type || ls_product_type.length === 0
          ? "At least one LS product type is required"
          : "",
    };
  }, [
    showValidation,
    pluginName,
    version,
    tags,
    description1,
    description2,
    ls_version,
    type,
    author,
    productType,
    ls_product_type,
  ]);

  const { data: vendorproductdata } = useQuery(["loadJsonData"], () =>
    loadJsonData("/static/ls/json/device-vendor-product-mappingDict.json"),
  );

  // take all types from vendorproductdata keys
  const allTypes = vendorproductdata
    ? Object.keys(vendorproductdata)
        .map((key) => ({ label: key, value: key }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  // Handle delete logo click
  const deleteLogo = () => {
    setOpenConfirmation(true);
  };

  // Handle logo deletion
  const handleDeleteLogo = () => {
    onChange({ pluginLogo: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setOpenConfirmation(false);
  };

  // Handle logo upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 2 * 1024 * 1024;

    if (file.size > MAX_SIZE) {
      toast({
        variant: "destructive",
        title: "Upload Error",
        description: "Image size exceeds 2MB. Please upload a smaller image.",
        duration: 3000,
      });

      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setCropSourceImage(reader.result as string);
      setOpenCropDialog(true);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    reader.readAsDataURL(file);
  };

  // Tag input handlers
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && tagInput.trim() !== "") {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        onChange({ tags: [...tags, tagInput.trim()] });
      }
      setTagInput("");
    }
  };

  // Remove tag handler
  const removeTag = (tagToRemove: string) => {
    onChange({ tags: tags.filter((tag) => tag !== tagToRemove) });
  };

  // Preview logo handler
  const handlePreviewLogo = () => {
    if (pluginLogo) {
      setPreviewLogo(true);
    }
  };

  // Normalized pluginDependency
  const normalizedPluginDependency = Array.isArray(pluginDependency)
    ? pluginDependency
    : pluginDependency
      ? [pluginDependency]
      : [];

  return (
    <>
      <PreviewLogo
        open={previewLogo}
        handleClose={() => setPreviewLogo(false)}
        logo={pluginLogo || ""}
        onRecrop={() => {
          if (!pluginLogo) return;
          setCropSourceImage(pluginLogo);
          setPreviewLogo(false);
          setOpenCropDialog(true);
        }}
      />

      <PluginLogoCropDialog
        open={openCropDialog}
        imageSrc={cropSourceImage}
        onClose={() => setOpenCropDialog(false)}
        onApply={(croppedLogo) => {
          onChange({ pluginLogo: croppedLogo });
          setOpenCropDialog(false);
        }}
      />

      <Confirmation
        show={openConfirmation}
        text="Are you sure you want to delete the logo?"
        confirmText="Yes, Delete"
        rejectText="Cancel"
        confirmAction={handleDeleteLogo}
        rejectAction={() => setOpenConfirmation(false)}
        closeDialog={() => setOpenConfirmation(false)}
      />

      <div className="bg-background overflow-y-auto cursor-default h-[70vh]">
        <div className="p-3">
          <div className="grid grid-cols-2 gap-6">
            <div className="grid cols-span-1 gap-2">
              <div>
                <label className="text-sm ml-1 ">Plugin Name *</label>
                <input
                  required
                  type="text"
                  value={pluginName}
                  placeholder="Plugin Name"
                  className={`w-full p-2 rounded-xl border ${errors.pluginName ? "border-primary" : "border-secondary"} bg-background text-sm mt-1`}
                  onChange={(e) => onChange({ pluginName: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm ml-1 ">Plugin Version *</label>
                <input
                  required
                  type="text"
                  value={version}
                  placeholder="Enter version (eg. 1.0 , 1.0.0)"
                  className={`w-full p-2 rounded-xl border ${errors.version ? "border-primary" : "border-secondary"} bg-background text-sm mt-1`}
                  onChange={(e) => {
                    // Filter out any characters that are not numbers or dots
                    const filtered = e.target.value.replace(/[^0-9.]/g, "");
                    onChange({ version: filtered });
                  }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 ">Tags *</label>
                  <InfoTooltip text="Add relevant tags to categorize your plugin (eg. 'Action', 'Block IP'). Press Enter after typing each tag to add it to the list." />
                </div>
                <div
                  className={`w-full p-2 rounded-xl border ${errors.tags ? "border-primary" : "border-secondary"} bg-background text-sm mt-1`}
                >
                  <div className="flex flex-wrap gap-2 ">
                    {tags?.map((tag, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-secondary px-2 py-1 rounded-lg text-sm"
                      >
                        {tag}
                        <span
                          onClick={() => removeTag(tag)}
                          className="ml-2 cursor-pointer text-primary hover:text-muted-foreground font-bold"
                        >
                          ×
                        </span>
                      </div>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Enter tag and press Enter"
                    className="w-full bg-transparent focus:outline-none border-secondary mt-2"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 ">Plugin Description *</label>
                  <InfoTooltip text="Provide a brief overview of what the plugin does, showing for the users. Minimum length should be 15 characters for clarity and completeness." />
                </div>
                <textarea
                  required
                  value={description1}
                  placeholder="Description "
                  rows={4}
                  maxLength={500}
                  className={`w-full p-2 rounded-xl border ${errors.description1 ? "border-primary" : "border-secondary"} bg-background text-sm mt-1`}
                  onChange={(e) => onChange({ description1: e.target.value })}
                />
                <div className="text-xs text-muted-foreground text-right">
                  {description1.length}/500 characters
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1">
                    Plugin Development Description *
                  </label>
                  <InfoTooltip text="Provide a brief overview of what the plugin does, showing for the creators. Minimum length should be 15 characters for clarity and completeness." />
                </div>
                <textarea
                  required
                  rows={4}
                  value={description2}
                  placeholder="Description 2"
                  maxLength={500}
                  className={`w-full p-2 rounded-xl border ${errors.description2 ? "border-primary" : "border-secondary"} bg-background text-sm mt-1`}
                  onChange={(e) => onChange({ description2: e.target.value })}
                />
                <div className="text-xs text-muted-foreground text-right ">
                  {description2.length}/500 characters
                </div>
              </div>
              <div>
                <label className="text-sm ml-1 mb-1">LS Version *</label>
                <Select
                  value={ls_version.minimum}
                  onValueChange={(value) =>
                    onChange({ ls_version: { minimum: value } })
                  }
                >
                  <SelectTrigger
                    className={`w-full rounded-xl border ${errors.ls_version ? "border-primary" : "border-secondary"} bg-background text-sm`}
                  >
                    <SelectValue placeholder="Select LS Version" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectGroup>
                      <SelectItem value="4.7.7">4.7.7</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid cols-span-1 gap-2 mt-1">
              {isAdminUser && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm mb-1">Type *</label>
                    <InfoTooltip text="Select the type of plugin whether it's a custom or generic plugin." />
                  </div>
                  <Select
                    onValueChange={(value) => onChange({ type: value })}
                    value={type}
                  >
                    <SelectTrigger
                      className={`w-full rounded-xl border ${errors.type ? "border-primary" : "border-secondary"} bg-background text-sm`}
                    >
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="custom">Custom</SelectItem>
                        <SelectItem value="generic">Generic</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-sm ml-1 ">Author *</label>
                <input
                  required
                  type="text"
                  value={author}
                  placeholder="Author"
                  className={`w-full p-2 rounded-xl border ${errors.author ? "border-primary" : "border-secondary"} bg-background text-sm`}
                  onChange={(e) => onChange({ author: e.target.value })}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 mb-1">Product Type *</label>
                  <InfoTooltip text="Specify the type of product this plugin is associated with." />
                </div>
                <div
                  className={`rounded-2xl overflow-hidden border ${errors.productType ? "border-primary" : "border-secondary"} bg-background`}
                >
                  <CustomSelect
                    data={allTypes}
                    placeholder="Select Product Type"
                    onSelect={(value) => {
                      onChange({
                        productType: Array.isArray(value) ? value[0] : value,
                      });
                    }}
                    defaultValue={productType}
                    multiSelect={false}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 mb-1">LS Product Type *</label>
                  <InfoTooltip text="Select the LinkShadow product types that this plugin is compatible with. You can select multiple options." />
                </div>
                <div
                  className={`rounded-2xl overflow-hidden border ${errors.ls_product_type ? "border-primary" : "border-secondary"} bg-background`}
                >
                  <CustomSelect
                    data={LS_PRODUCT_TYPE}
                    placeholder="Select LS product type"
                    onSelect={(selected) =>
                      onChange({ ls_product_type: selected })
                    }
                    defaultValue={ls_product_type}
                    multiSelect
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 mb-1">
                    Plugin Dependency Name{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <InfoTooltip text="Name of any base plugins which are required for this plugin to work." />
                </div>
                <input
                  type="text"
                  placeholder="Plugin Dependency Name"
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm"
                  value={normalizedPluginDependency[0]?.name || ""}
                  onChange={(e) => {
                    onChange({
                      pluginDependency: [
                        { name: e.target.value, id: Date.now().toString() },
                      ],
                    });
                  }}
                />
              </div>

              <div className="flex items-start justify-between gap-4 rounded-xl border border-secondary bg-background p-4 shadow-sm">
                {/* Upload Section */}
                <div className="flex w-9/12 flex-col gap-2">
                  <label className="text-sm font-medium text-foreground">
                    Plugin Logo{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </label>

                  <label
                    htmlFor="plugin-logo"
                    className="group flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-secondary bg-muted/30 px-4 py-3 text-sm transition  hover:border-primary"
                  >
                    <UploadCloud
                      size={20}
                      className="text-muted-foreground transition group-hover:text-primary"
                    />

                    <span className="text-muted-foreground">
                      Click to {pluginLogo ? "update" : "upload"} Plugin Logo
                      {/* Plugin logo. */}
                      <span className="block text-xs group-hover:text-primary">
                        Accept .png, .jpg - up to 2MB.
                      </span>
                    </span>

                    <input
                      id="plugin-logo"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </label>
                </div>

                {/* Preview */}
                <div
                  className={`group relative h-24 w-24 cursor-pointer overflow-hidden rounded-xl border shadow-sm transition ${pluginLogo && "hover:shadow-md"}`}
                  onClick={handlePreviewLogo}
                >
                  <img
                    src={`${pluginLogo || process.env.STATIC_PATH + "/images/plugin-default.jpg"}`}
                    alt="Plugin Logo Preview"
                    className={`h-full w-full object-cover transition duration-300 ${pluginLogo ? "group-hover:scale-105 group-hover:blur-sm" : "cursor-not-allowed"}`}
                    //   onClick={!pluginLogo && handleLogoUpload()}
                  />

                  {pluginLogo && (
                    <div className="absolute inset-0 flex items-center justify-center bg-foregorund opacity-0 transition group-hover:opacity-100">
                      <span className="text-xs font-medium text-background">
                        View
                      </span>
                    </div>
                  )}

                  {pluginLogo && (
                    <Trash2
                      size={20}
                      className="
                    absolute right-1 top-1 rounded-full p-1
                    bg-transparent
                    text-background
                    opacity-0 shadow
                    transition
                    group-hover:opacity-100
                    group-hover:bg-foreground
                    hover:text-primary
                    "
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLogo();
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
